import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import {
  StudioSceneBackgroundDefinition,
  StudioScene,
  StudioImage,
  StudioVideo,
  ImageAnimation,
} from '../../element-editor/store/studio/studio.types';
import {
  selectStudioImages,
  selectStudioVideos,
} from '../../element-editor/store/studio/studio.selector';
import { BackgroundType } from '../../element-editor/store/studio/background/background.service';
import { GradientDirection } from '../../element-editor/shape/shapes/primitive/rectangle-element';
import { cloneDeep } from 'lodash';
import { ShapeService } from '../../element-editor/shape/shape.service';
import { addStudioImage } from '../../element-editor/store/studio/studio.actions';

export type BgUIType = 'image' | 'gradient' | 'solid' | 'video';

@Component({
  selector: 'nw-background-settings-modal',
  templateUrl: './background-settings-modal.component.html',
  styleUrls: ['./background-settings-modal.component.scss'],
})
export class BackgroundSettingsModalComponent implements OnInit, OnDestroy {
  @Input() scene: StudioScene | null = null;

  @Output() backgroundChange =
    new EventEmitter<StudioSceneBackgroundDefinition>();
  @Output() cropChange = new EventEmitter<ImageAnimation['crop']>();
  @Output() cancel = new EventEmitter<void>();

  // Type selector
  types: { label: string; key: BgUIType; icon: string }[] = [
    { label: 'Image', key: 'image', icon: 'image' },
    { label: 'Gradient', key: 'gradient', icon: 'gradient' },
    { label: 'Solid', key: 'solid', icon: 'format_color_fill' },
    { label: 'Video', key: 'video', icon: 'videocam' },
  ];
  selectedType: BgUIType = 'image';

  GradientDirection = GradientDirection;

  // Crop mode
  showCrop = false;

  // Images from store
  images: StudioImage[] = [];
  private imagesSub: Subscription;

  // Videos from store
  videos: StudioVideo[] = [];
  private videosSub: Subscription;

  // Working background definition
  get bgDef(): StudioSceneBackgroundDefinition | undefined {
    return this.previewScene?.background;
  }

  // Gradient form
  gradientColors: string[] = ['#ff6a00', '#006aff'];
  gradientDirection: GradientDirection = GradientDirection.Diagonal;

  // Solid fill
  solidColor = '#5b9bd5';

  // Video URL
  videoUrl = '';

  // Preview scene
  previewScene: StudioScene | null = null;

  constructor(
    private readonly store: Store,
    private readonly shapeService: ShapeService,
  ) {}

  ngOnInit() {
    this.imagesSub = this.store
      .select(selectStudioImages)
      .subscribe(imgs => {
        this.images = Object.values(imgs || {});
      });

    this.videosSub = this.store
      .select(selectStudioVideos)
      .subscribe(vids => {
        this.videos = Object.values(vids || {});
      });

    this.initFromScene();
  }

  private initFromScene() {
    if (!this.scene) return;
    this.previewScene = cloneDeep(this.scene);

    const bg = this.scene.background;
    if (!bg) {
      this.selectedType = 'image';
      return;
    }

    switch (bg.type) {
      case 'image':
        this.selectedType = 'image';
        break;
      case 'gradient':
        this.selectedType = 'gradient';
        if (bg.gradient?.colors?.length) {
          this.gradientColors = [...bg.gradient.colors];
        }
        if (bg.gradient?.direction) {
          this.gradientDirection = bg.gradient.direction;
        }
        break;
      case 'video':
        this.selectedType = 'video';
        if (bg.video?.url) {
          this.videoUrl = bg.video.url;
        }
        break;
      default:
        this.selectedType = 'solid';
        break;
    }
  }

  selectType(type: BgUIType) {
    this.selectedType = type;
    this.showCrop = false;
  }

  // --- Image ---

  get selectedImageUrl(): string | null {
    return this.bgDef?.image?.url ?? null;
  }

  selectImage(image: StudioImage) {
    const def: StudioSceneBackgroundDefinition = {
      type: 'image',
      image: { url: image.url, fit: 'cover' },
    };
    if (this.bgDef?.animation) {
      def.animation = this.bgDef.animation;
    }
    this.emitChange(def);
  }

  onImageUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    Array.from(input.files).forEach(file => {
      const id = this.shapeService.getNewID();
      const reader = new FileReader();
      reader.onload = () => {
        const url = reader.result as string;
        this.shapeService.cs.httpService.saveFileToS3(file, id).then(() => {
          const cdnUrl = `https://private.cdn.inwedio.com/${id}`;
          const image: StudioImage = {
            id,
            s3Id: id,
            url: cdnUrl,
            fileName: file.name,
          };
          this.store.dispatch(addStudioImage({ image }));
          this.selectImage(image);
        });
      };
      reader.readAsDataURL(file);
    });

    input.value = '';
  }

  toggleCrop() {
    this.showCrop = !this.showCrop;
  }

  onCropChange(crop: ImageAnimation['crop']) {
    this.cropChange.emit(crop);
  }

  // --- Gradient ---

  onGradientColorChange(index: number, color: string) {
    this.gradientColors[index] = color;
    this.applyGradient();
  }

  onGradientDirectionChange(dir: GradientDirection) {
    this.gradientDirection = dir;
    this.applyGradient();
  }

  private applyGradient() {
    const start: [number, number] = [0, 0];
    let end: [number, number];
    switch (this.gradientDirection) {
      case GradientDirection.Horizontal:
        end = [1, 0];
        break;
      case GradientDirection.Vertical:
        end = [0, 1];
        break;
      case GradientDirection.Diagonal:
      default:
        end = [1, 1];
        break;
    }

    const def: StudioSceneBackgroundDefinition = {
      type: 'gradient',
      gradient: {
        type: 'linear',
        direction: this.gradientDirection,
        colors: [...this.gradientColors],
        start,
        end,
      },
    };
    this.emitChange(def);
  }

  // --- Solid ---

  onSolidColorChange(color: string) {
    this.solidColor = color;
    const def: StudioSceneBackgroundDefinition = {
      type: 'gradient',
      gradient: {
        type: 'linear',
        colors: [color, color],
        start: [0, 0],
        end: [1, 0],
      },
    };
    this.emitChange(def);
  }

  // --- Video ---

  selectVideo(video: StudioVideo) {
    const url = video.url || `https://private.cdn.inwedio.com/${video.s3Id}`;
    this.videoUrl = url;
    const def: StudioSceneBackgroundDefinition = {
      type: 'video',
      video: { url },
    };
    this.emitChange(def);
  }

  onVideoUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    const id = this.shapeService.getNewID();

    this.shapeService.cs.httpService.saveFileToS3(file, id).then(() => {
      const cdnUrl = `https://private.cdn.inwedio.com/${id}`;
      this.videoUrl = cdnUrl;
      const def: StudioSceneBackgroundDefinition = {
        type: 'video',
        video: { url: cdnUrl },
      };
      this.emitChange(def);
    });

    input.value = '';
  }

  onVideoUrlChange(url: string) {
    this.videoUrl = url;
    if (!url) return;
    const def: StudioSceneBackgroundDefinition = {
      type: 'video',
      video: { url },
    };
    this.emitChange(def);
  }

  // --- Emit ---

  private emitChange(def: StudioSceneBackgroundDefinition) {
    this.backgroundChange.emit(def);
    if (this.previewScene) {
      this.previewScene = { ...this.previewScene, background: def };
    }
  }

  getImageThumbUrl(image: StudioImage): string {
    const url = image.url || '';
    if (url.startsWith('http')) return url;
    return `https://private.cdn.inwedio.com/${url}`;
  }

  onClose() {
    this.cancel.emit();
  }

  ngOnDestroy() {
    this.imagesSub?.unsubscribe();
    this.videosSub?.unsubscribe();
  }
}
